const createTileBuildingsLayerPaint = (scoreField, colorStops) => ({
  "circle-color": [
    "case",
    ["==", ["get", "total_score"], null],
    "rgba(0, 0, 0, 0.3)",
    ["interpolate", ["linear"], ["get", scoreField], ...colorStops.flat()],
  ],
  "circle-opacity": [
    "step",
    ["zoom"],
    ["case", ["==", ["get", "total_score"], null], 0, 0.8],
    13.5,
    ["case", ["==", ["get", "total_score"], null], 0.8, 0.8],
  ],
  "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 2, 14, 4, 20, 15],
});

export const tileBuildingsLayerPaintCombined = createTileBuildingsLayerPaint(
  "total_score",
  [
    [1, "#ffffb2"],
    [3.25, "#fed266"],
    [5.5, "#fd9b43"],
    [7.75, "#f45629"],
    [10, "#d41a23"],
  ]
);

export const tileBuildingsLayerPaintNorth = createTileBuildingsLayerPaint(
  "north_score",
  [
    [0.5, "#ffffb2"],
    [1.625, "#fed266"],
    [2.75, "#fd9b43"],
    [3.875, "#f45629"],
    [5, "#d41a23"],
  ]
);

export const tileBuildingsLayerPaintSouth = createTileBuildingsLayerPaint(
  "south_score",
  [
    [0.5, "#ffffb2"],
    [1.625, "#fed266"],
    [2.75, "#fd9b43"],
    [3.875, "#f45629"],
    [5, "#d41a23"],
  ]
);

export const tilePathwaysLayerPaint = {
  "line-color": "#a83232",
  "line-width": 1,
  "line-opacity": {
    property: "opacity",
    stops: [
      [{ zoom: 13, value: 1 }, 0.1],
      [{ zoom: 16, value: 1 }, 0.4],
      [{ zoom: 20, value: 1 }, 0.7],
    ],
  },
};

export const tileConflagrationBlocksLayerPaint = {
  "fill-color": "gray",
  "fill-outline-color": "black",
  "fill-opacity": 0.5,
};

export const createTileSourceUrl = (version) => {
  const XYLOPLAN_MAP_API_URL = process.env.REACT_APP_XYLOPLAN_MAP_API_URL;
  const XYLOPLAN_MAP_API_URL_SIGNATURE =
    process.env.REACT_APP_XYLOPLAN_MAP_API_URL_SIGNATURE;
  return `${XYLOPLAN_MAP_API_URL}/tiles/${version}/{z}/{x}/{y}.pbf${XYLOPLAN_MAP_API_URL_SIGNATURE}`;
};

export const addRiskMapSource = (map, version) => {
  if (!map) return;

  const tilesUrl = createTileSourceUrl(version);

  if (!map.getSource("xyloplan_data")) {
    map.addSource("xyloplan_data", {
      type: "vector",
      tiles: [tilesUrl],
      minzoom: 11,
      maxzoom: 16,
    });
    return false;
  } else {
    const source = map.getSource("xyloplan_data");
    if (source && source.tiles[0] !== tilesUrl) {
      ["buildings", "statewide-pathways", "conflagration-blocks"].forEach(
        (layerId) => {
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        }
      );
      map.removeSource("xyloplan_data");
      map.addSource("xyloplan_data", {
        type: "vector",
        tiles: [tilesUrl],
        minzoom: 11,
        maxzoom: 16,
      });
      return true;
    }
  }
  return false;
};

export const getScenarioFilter = (scenario) => {
  if (scenario === "combined") return null;

  return [
    "==",
    ["get", "scenario"],
    scenario === "north-east" ? "north_and_east_wind" : "south_and_west_wind",
  ];
};

export const getBuildingsPaint = (scenario) => {
  switch (scenario) {
    case "north-east":
      return tileBuildingsLayerPaintNorth;
    case "south-west":
      return tileBuildingsLayerPaintSouth;
    default:
      return tileBuildingsLayerPaintCombined;
  }
};

export const addRiskMapLayers = (map, scenario, layers) => {
  if (!map) return;

  const scenarioFilter = getScenarioFilter(scenario);

  if (!map.getLayer("conflagration-blocks")) {
    map.addLayer({
      id: "conflagration-blocks",
      type: "fill",
      source: "xyloplan_data",
      "source-layer": "conflagration_blocks",
      paint: tileConflagrationBlocksLayerPaint,
      layout: {
        visibility: layers.includes("conflagration-blocks")
          ? "visible"
          : "none",
      },
    });
  }

  if (!map.getLayer("statewide-pathways")) {
    map.addLayer({
      id: "statewide-pathways",
      type: "line",
      source: "xyloplan_data",
      "source-layer": "pathways",
      paint: tilePathwaysLayerPaint,
      layout: {
        visibility: layers.includes("fire-pathways") ? "visible" : "none",
      },
      ...(scenarioFilter && { filter: scenarioFilter }),
    });
  }

  if (!map.getLayer("buildings")) {
    const buildingsPaint = getBuildingsPaint(scenario);

    map.addLayer({
      id: "buildings",
      type: "circle",
      source: "xyloplan_data",
      "source-layer": "buildings",
      paint: buildingsPaint,
      layout: {
        visibility: layers.includes("risk-rating") ? "visible" : "none",
      },
    });
  }
};

export const updateLayerVisibility = (map, layers) => {
  if (!map) return;

  ["buildings", "statewide-pathways", "conflagration-blocks"].forEach(
    (layerId) => {
      if (map.getLayer(layerId)) {
        const shouldBeVisible =
          (layerId === "buildings" && layers.includes("risk-rating")) ||
          (layerId === "statewide-pathways" &&
            layers.includes("fire-pathways")) ||
          (layerId === "conflagration-blocks" &&
            layers.includes("conflagration-blocks"));

        map.setLayoutProperty(
          layerId,
          "visibility",
          shouldBeVisible ? "visible" : "none"
        );
      }
    }
  );
};

export const updateScenarioFilters = (map, scenario) => {
  if (!map) return;

  const scenarioFilter = getScenarioFilter(scenario);

  if (map.getLayer("statewide-pathways")) {
    if (scenarioFilter) {
      map.setFilter("statewide-pathways", scenarioFilter);
    } else {
      map.setFilter("statewide-pathways", null);
    }
  }

  if (map.getLayer("buildings")) {
    const buildingsPaint = getBuildingsPaint(scenario);

    Object.entries(buildingsPaint).forEach(([property, value]) => {
      map.setPaintProperty("buildings", property, value);
    });
  }
};

export const removeRiskMapLayers = (map) => {
  if (!map) return;

  ["buildings", "statewide-pathways", "conflagration-blocks"].forEach(
    (layerId) => {
      if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
    }
  );

  if (map.getSource("xyloplan_data")) {
    map.removeSource("xyloplan_data");
  }
};
